import { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { TLayoutItems, TVideoControls, TVideoObjectFit } from '@src/types';
import { checkMediaDevicePermission } from '@src/helpers';
import { MEDIA_TYPE } from '@src/constants';
import { usePeerActions } from '@src/hooks';
import { useMediaSettingsStore, useRoomActionsStore } from '@src/stores';
import { useShallow } from 'zustand/react/shallow';
import useEnumerateDevices from '../../hooks/useEnumerateDevices';
import useRoomContext from '../useRoomContext';

type TMenuItems = 'audio' | 'video' | 'aspect' | 'general';

interface ISettingsContextType {
  selectedLayout: TLayoutItems;
  selectedMenuItem: TMenuItems;
  selectedAspectRatio: number;
  selectedVideoObjectFit: TVideoObjectFit;
  selectedVideoControls: TVideoControls;

  handleChangeLayout: (layout: TLayoutItems) => void;
  handleChangeSelectedItem: (item: TMenuItems) => void;
  handleChangeAspectRatio: (ratio: number) => void;
  handleChangeVideoControls: (videoControls: TVideoControls) => void;
  handleChangeVideoObjectFit: (objectFit: TVideoObjectFit) => void;
}

interface ISettingsContextProviderProps {
  children: ReactNode;
}

const settingsContextDefaultValue: ISettingsContextType = {
  selectedLayout: 'TILED',
  selectedMenuItem: 'audio',
  selectedAspectRatio: 0.75,
  selectedVideoObjectFit: 'cover',
  selectedVideoControls: 'off',

  handleChangeLayout: () => {},
  handleChangeSelectedItem: () => {},
  handleChangeAspectRatio: () => {},
  handleChangeVideoControls: () => {},
  handleChangeVideoObjectFit: () => {},
};

const SettingsContext = createContext<ISettingsContextType>(settingsContextDefaultValue);

export const SettingsContextProvider: React.FC<ISettingsContextProviderProps> = ({ children }) => {
  const [selectedLayout, setSelectedLayout] = useState<TLayoutItems>(settingsContextDefaultValue.selectedLayout);
  const [selectedMenuItem, setSelectedMenuItem] = useState<TMenuItems>(settingsContextDefaultValue.selectedMenuItem);
  const [selectedAspectRatio, setSelectedAspectRatio] = useState<number>(
    settingsContextDefaultValue.selectedAspectRatio
  );
  const [selectedVideoObjectFit, setSelectedVideoObjectFit] = useState<TVideoObjectFit>(
    settingsContextDefaultValue.selectedVideoObjectFit
  );
  const [selectedVideoControls, setSelectedVideoControls] = useState<TVideoControls>(
    settingsContextDefaultValue.selectedVideoControls
  );

  // 1. Contexts
  const { currentPeer, isMobileDevice } = useRoomContext();

  // 2. Zustand stores
  const { isMicClosed, isCamClosed, setMicSettings, setCamSettings, micSettings, camSettings } = useRoomActionsStore(
    useShallow((state) => ({
      isMicClosed: state.micSettings.isMicClosed,
      isCamClosed: state.camSettings.isCamClosed,
      micSettings: state.micSettings,
      camSettings: state.camSettings,
      setMicSettings: state.setMicSettings,
      setCamSettings: state.setCamSettings,
    }))
  );

  const { setMediaSettings, setIsEnumerateVideoDevices, setIsEnumerateAudioDevices } = useMediaSettingsStore(
    useShallow((state) => ({
      setMediaSettings: state.setMediaSettings,
      setIsEnumerateVideoDevices: state.setIsEnumerateVideoDevices,
      setIsEnumerateAudioDevices: state.setIsEnumerateAudioDevices,
    }))
  );

  // 3. Custom hooks
  const { handleUpdatePeerInfo, closeProducer } = usePeerActions(currentPeer);

  const { initEnumerateAudioDevices, initEnumerateVideoDevices } = useEnumerateDevices();

  // 4. Callbacks
  const handleChangeSelectedItem = useCallback((selectedItem: TMenuItems) => {
    setSelectedMenuItem(selectedItem);
  }, []);

  const handleChangeAspectRatio = useCallback((aspectRatio: number) => {
    setSelectedAspectRatio(aspectRatio);
  }, []);

  const handleChangeVideoObjectFit = useCallback((objectFit: TVideoObjectFit) => {
    setSelectedVideoObjectFit(objectFit);
  }, []);

  const handleChangeVideoControls = useCallback((videoControls: TVideoControls) => {
    setSelectedVideoControls(videoControls);
  }, []);

  const handleChangeLayout = useCallback((selectedLayout: TLayoutItems) => {
    setSelectedLayout(selectedLayout);
  }, []);

  const handleVisibilityChange = useCallback(async () => {
    if (!document.hidden) {
      const videoPermission = await checkMediaDevicePermission('camera' as PermissionName);
      const audioPermission = await checkMediaDevicePermission('microphone' as PermissionName);

      const { peer_name = '', peer_id = '' } = currentPeer?.peer_info || {};

      if (!audioPermission) {
        setIsEnumerateAudioDevices(false);
        setMediaSettings({
          audioInputDevices: [],
          speakerDevices: [],
          ...{ selectedAudioInputDevice: null, selectedSpeakerDevice: null },
        });
        if (!isMicClosed && currentPeer) {
          closeProducer(MEDIA_TYPE.audio);
          setMicSettings({ ...micSettings, isMicClosed: true });
          handleUpdatePeerInfo({ peer_name, peer_id, type: 'audio', status: false });
        }
      }

      if (!videoPermission) {
        setIsEnumerateVideoDevices(false);
        setMediaSettings({
          videoInputDevices: [],
          ...{ selectedVideoInputDevice: null },
        });
        if (!isCamClosed && currentPeer) {
          closeProducer(MEDIA_TYPE.video);
          setCamSettings({ ...camSettings, isCamClosed: true });
          handleUpdatePeerInfo({ peer_name, peer_id, type: 'video', status: false });
        }
      }

      if (audioPermission && !useMediaSettingsStore.getState().isEnumerateAudioDevices) {
        initEnumerateAudioDevices();
      }

      if (videoPermission && !useMediaSettingsStore.getState().isEnumerateVideoDevices) {
        initEnumerateVideoDevices();
      }

      setMediaSettings({ isAudioAllowed: audioPermission });
      setMediaSettings({ isVideoAllowed: videoPermission });
    }
  }, [
    camSettings,
    closeProducer,
    currentPeer,
    handleUpdatePeerInfo,
    initEnumerateAudioDevices,
    initEnumerateVideoDevices,
    isCamClosed,
    isMicClosed,
    micSettings,
    setCamSettings,
    setIsEnumerateAudioDevices,
    setIsEnumerateVideoDevices,
    setMediaSettings,
    setMicSettings,
  ]);

  // 5. useEffects
  useEffect(() => {
    if (!isMobileDevice) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('focus', handleVisibilityChange);
    }
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [handleVisibilityChange, isMobileDevice]);

  const contextValue = useMemo(() => {
    return {
      selectedLayout,
      selectedMenuItem,
      selectedAspectRatio,
      selectedVideoControls,
      selectedVideoObjectFit,
      handleChangeVideoObjectFit,
      handleChangeVideoControls,
      handleChangeSelectedItem,
      handleChangeAspectRatio,
      handleChangeLayout,
    };
  }, [
    selectedLayout,
    selectedMenuItem,
    selectedAspectRatio,
    selectedVideoControls,
    selectedVideoObjectFit,
    handleChangeVideoObjectFit,
    handleChangeVideoControls,
    handleChangeSelectedItem,
    handleChangeAspectRatio,
    handleChangeLayout,
  ]);

  return <SettingsContext.Provider value={contextValue}>{children}</SettingsContext.Provider>;
};

export default SettingsContext;
